import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../auth/AuthContext';
import {
  CategoryNode,
  CategorySalesPeriod,
  CategorySalesReport,
  TenantCategorySales,
  getCategorySalesReport,
} from '../../api';
import RoughBar from '../widgets/charts/RoughBar';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; report: CategorySalesReport };

type Granularity = 'root' | 'leaf';

// Active drill-down: a top-level category the user clicked into, so the
// charts show only its descendant (leaf-level) categories. `null` = the
// normal top-level / leaf view.
type DrillState = { id: string; label: string } | null;

// Bucket key used for sold items that have no category set. Chosen so it
// can't collide with a real category id (those are UUIDs / numeric-ish).
const UNCATEGORIZED = '__uncategorized__';

// Root marker stored on top-level categories (mirrors the backend's
// ROOT_PARENT_ID). A node is a root when its parent is this, absent, or
// points at an id we don't have.
const ROOT_PARENT_ID = '1';

// The only tenant tab that shows the cross-tenant Custom/Pending bars.
// They're unattributable, but overwhelmingly originate at The Back Room,
// so surfacing them anywhere else just buries a smaller shop's categories.
const UNCATALOGUED_TENANT_SLUG = 'backroom';

const PERIODS: { key: CategorySalesPeriod; label: string }[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: '3months', label: '3 Months' },
  { key: '6months', label: '6 Months' },
  { key: 'year', label: 'Year' },
];

// Show at most this many bars per chart; the long tail is summarised in
// a note beneath the chart. Leaf view can otherwise produce a very wide,
// unreadable axis.
const MAX_BARS = 20;

const CategorySalesReportPage = () => {
  const { idToken } = useAuth();
  const [period, setPeriod] = useState<CategorySalesPeriod>('month');
  const [granularity, setGranularity] = useState<Granularity>('root');
  const [tenantIdx, setTenantIdx] = useState(0);
  const [drill, setDrill] = useState<DrillState>(null);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  // Any change to the window, granularity, or shop resets a drill-down —
  // a leaf breakdown of the previous parent makes no sense in a new frame.
  const changePeriod = (v: CategorySalesPeriod | null) => {
    if (v) {
      setPeriod(v);
      setDrill(null);
    }
  };
  const changeGranularity = (v: Granularity | null) => {
    if (v) {
      setGranularity(v);
      setDrill(null);
    }
  };
  const changeTenant = (i: number) => {
    setTenantIdx(i);
    setDrill(null);
  };

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    getCategorySalesReport(idToken, period)
      .then((report) => {
        if (!cancelled) setState({ kind: 'ready', report });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message:
              (err as Error)?.message || 'failed to load sales-by-category report',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, period]);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
        Sales by category
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
        Units sold and gross revenue bucketed by category over the selected
        window — in-store (reconciled Square sales) plus fulfilled/paid web
        orders. Revenue is pre-tax, pre-discount.
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 3 }}
        alignItems={{ sm: 'center' }}
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(_, v) => changePeriod(v)}
          aria-label="period"
        >
          {PERIODS.map((p) => (
            <ToggleButton key={p.key} value={p.key}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={granularity}
          onChange={(_, v) => changeGranularity(v)}
          aria-label="granularity"
        >
          <ToggleButton value="root">Top-level</ToggleButton>
          <ToggleButton value="leaf">Leaf</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {state.kind === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {state.kind === 'error' && <Alert severity="error">{state.message}</Alert>}

      {state.kind === 'ready' && (
        <ReadyView
          report={state.report}
          granularity={granularity}
          tenantIdx={tenantIdx}
          onTenantChange={changeTenant}
          drill={drill}
          onDrill={setDrill}
        />
      )}
    </Box>
  );
};

type BucketRow = {
  id: string;
  label: string;
  units: number;
  revenueCents: number;
  /** True for the cross-tenant "Custom" / "Pending" bars. They rank by
   *  value alongside a shop's own category bars (Custom is usually the
   *  tallest, so it lands leftmost); the flag only excludes them from the
   *  "top N of M categories" count. Distinguished by their "(all shops)"
   *  label. */
  synthetic?: boolean;
  /** True (top-level view only) when this category has sub-categories, so
   *  clicking it drills in. Marked with a ▸ on the axis so it's clear
   *  which bars are clickable — many roots have no children. */
  drillable?: boolean;
};

// Build the cross-tenant uncatalogued bars appended to every shop's
// charts: Custom (typed register lines, never categorisable) and Pending
// (has a Square item, awaiting reconciliation). Zero-volume populations
// are dropped. These carry no tenant, so the same bars show on each tab —
// the "(all shops)" label makes that explicit.
function uncataloguedBuckets(
  uncat: CategorySalesReport['uncatalogued'],
): BucketRow[] {
  const rows: BucketRow[] = [
    {
      id: '__custom__',
      label: 'Custom (all shops)',
      units: uncat.custom_units,
      revenueCents: uncat.custom_revenue_cents,
      synthetic: true,
    },
    {
      id: '__pending__',
      label: 'Pending recon. (all shops)',
      units: uncat.pending_units,
      revenueCents: uncat.pending_revenue_cents,
      synthetic: true,
    },
  ];
  return rows.filter((r) => r.units > 0 || r.revenueCents > 0);
}

// Walk `tree` into a fast id→node map and a memoised root resolver, then
// fold each tenant sales entry into a bucket. Three modes:
//   - drilled (drillRoot set): only entries whose root ancestor IS that
//     root, keyed by their own (leaf-level) category — the granular
//     breakdown of one top-level category.
//   - Top-level: keyed by each entry's root ancestor.
//   - Leaf: keyed by each entry's own category.
function bucketize(
  tenant: TenantCategorySales,
  granularity: Granularity,
  drillRoot: string | null,
): BucketRow[] {
  const byId = new Map<string, CategoryNode>();
  for (const node of tenant.tree) byId.set(node.id, node);

  const rootCache = new Map<string, string>();
  const rootOf = (startId: string): string => {
    const cached = rootCache.get(startId);
    if (cached) return cached;
    let cur = startId;
    for (let guard = 0; guard < 64; guard += 1) {
      const node = byId.get(cur);
      if (!node) break; // unknown id — treat as its own root
      const parent = node.parent_id;
      if (!parent || parent === ROOT_PARENT_ID || !byId.has(parent)) break;
      cur = parent;
    }
    rootCache.set(startId, cur);
    return cur;
  };

  const nameOf = (id: string): string => {
    if (id === UNCATEGORIZED) return 'Uncategorized';
    return byId.get(id)?.name ?? 'Unknown';
  };

  const buckets = new Map<string, BucketRow>();
  for (const entry of tenant.categories) {
    const direct = entry.category_id ?? UNCATEGORIZED;

    let key: string;
    if (drillRoot) {
      // Only sales inside the drilled root, broken out by their own
      // category. Uncategorized has no root, so it's excluded.
      if (direct === UNCATEGORIZED || rootOf(direct) !== drillRoot) continue;
      key = direct;
    } else if (direct === UNCATEGORIZED) {
      key = UNCATEGORIZED;
    } else {
      key = granularity === 'root' ? rootOf(direct) : direct;
    }

    const existing = buckets.get(key);
    if (existing) {
      existing.units += entry.units;
      existing.revenueCents += entry.revenue_cents;
    } else {
      buckets.set(key, {
        id: key,
        label: nameOf(key),
        units: entry.units,
        revenueCents: entry.revenue_cents,
      });
    }
  }
  return Array.from(buckets.values());
}

// Ids that are a parent of at least one real category — i.e. drilling
// into them yields a finer breakdown. Used to gate which top-level bars
// are clickable.
function parentIds(tenant: TenantCategorySales): Set<string> {
  const parents = new Set<string>();
  for (const node of tenant.tree) {
    if (node.parent_id && node.parent_id !== ROOT_PARENT_ID) {
      parents.add(node.parent_id);
    }
  }
  return parents;
}

const ReadyView = ({
  report,
  granularity,
  tenantIdx,
  onTenantChange,
  drill,
  onDrill,
}: {
  report: CategorySalesReport;
  granularity: Granularity;
  tenantIdx: number;
  onTenantChange: (idx: number) => void;
  drill: DrillState;
  onDrill: (d: DrillState) => void;
}) => {
  // Clamp the tab index — a tenant list change (unlikely mid-session)
  // shouldn't leave us pointing past the end.
  const idx = Math.min(tenantIdx, Math.max(0, report.tenants.length - 1));
  const tenant = report.tenants[idx];

  // Clicking a top-level bar drills in. Only in the top-level view (not
  // leaf, not already drilled), and only for categories that actually
  // have sub-categories.
  const canDrill = !drill && granularity === 'root';

  // Category ids that have children — the ones worth drilling into.
  const parents = useMemo(
    () => (tenant ? parentIds(tenant) : new Set<string>()),
    [tenant],
  );

  // When drilled, show only the clicked root's leaf breakdown — no
  // synthetic bars (they belong to no category, so they can't be inside a
  // root). Otherwise: the shop's category bars, plus the cross-tenant
  // Custom/Pending bars ONLY on the Back Room tab (unattributable, but
  // overwhelmingly from there; elsewhere they'd bury a smaller shop). In
  // the top-level view, tag which bars are drillable so the chart can
  // flag them.
  const buckets = useMemo(() => {
    if (!tenant) return [];
    const base = bucketize(tenant, granularity, drill?.id ?? null);
    if (drill) return base;
    const withUncat =
      tenant.slug === UNCATALOGUED_TENANT_SLUG
        ? [...base, ...uncataloguedBuckets(report.uncatalogued)]
        : base;
    if (!canDrill) return withUncat;
    return withUncat.map((b) =>
      !b.synthetic && b.id !== UNCATEGORIZED && parents.has(b.id)
        ? { ...b, drillable: true }
        : b,
    );
  }, [tenant, granularity, drill, report.uncatalogued, canDrill, parents]);

  const handleClick = (b: BucketRow) => {
    if (b.drillable) onDrill({ id: b.id, label: b.label });
  };

  const hasUncatalogued = buckets.some((b) => b.synthetic);
  const revenueTitle = drill ? `Revenue — ${drill.label}` : 'Revenue by category';
  const unitsTitle = drill ? `Units sold — ${drill.label}` : 'Units sold by category';
  const revenueSub = drill
    ? `Gross sales (pre-tax) per sub-category within ${drill.label}, in dollars.`
    : 'Gross sales (pre-tax) per category, in dollars. Click a ▸ bar to see its sub-categories.';
  const unitsSub = drill
    ? `Total item count sold per sub-category within ${drill.label}.`
    : 'Total item count sold per category. Click a ▸ bar to see its sub-categories.';

  return (
    <Stack spacing={3}>
      <Tabs
        value={idx}
        onChange={(_, v) => onTenantChange(v)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {report.tenants.map((t) => (
          <Tab key={t.slug} label={t.label} />
        ))}
      </Tabs>

      {drill && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => onDrill(null)}
          >
            All categories
          </Button>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f2937' }}>
            {drill.label}
          </Typography>
        </Box>
      )}

      {!tenant || buckets.length === 0 ? (
        <Alert severity="info">
          {drill
            ? `No sub-category sales in ${drill.label} for this window.`
            : `No sales in this window for ${tenant?.label ?? 'this shop'}.`}
        </Alert>
      ) : (
        <>
          <CategoryChart
            title={revenueTitle}
            subtitle={revenueSub}
            buckets={buckets}
            metric="revenue"
            onCategoryClick={canDrill ? handleClick : undefined}
          />
          <CategoryChart
            title={unitsTitle}
            subtitle={unitsSub}
            buckets={buckets}
            metric="units"
            onCategoryClick={canDrill ? handleClick : undefined}
          />
          {hasUncatalogued && (
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              The <strong>(all shops)</strong> bars are cross-tenant in-store
              sales with no category. They carry no shop attribution, but the
              overwhelming majority originate at The Back Room, so they&apos;re
              shown here rather than split across shops: <strong>Custom</strong> =
              typed register lines with no catalog item (never categorisable);{' '}
              <strong>Pending recon.</strong> = has a Square item but not yet
              reconciled (moves into a category as the sweep runs).
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
};

const CategoryChart = ({
  title,
  subtitle,
  buckets,
  metric,
  onCategoryClick,
}: {
  title: string;
  subtitle: string;
  buckets: BucketRow[];
  metric: 'revenue' | 'units';
  onCategoryClick?: (b: BucketRow) => void;
}) => {
  const valueOf = (b: BucketRow) =>
    metric === 'revenue' ? Math.round(b.revenueCents) / 100 : b.units;

  const baseColor = metric === 'revenue' ? '#3b6fb0' : '#5d6b3a';

  // Everything — real categories plus the synthetic Custom/Pending bars —
  // ranks together by value, so the tallest bar is always leftmost (Custom
  // is usually the biggest). The top-N cap then applies to the combined
  // list; the "not shown" note below counts only real categories dropped.
  const sorted = [...buckets].sort((a, b) => valueOf(b) - valueOf(a));
  const shown = sorted.slice(0, MAX_BARS);
  const totalCategories = buckets.filter((b) => !b.synthetic).length;
  const shownCategories = shown.filter((b) => !b.synthetic).length;
  const hiddenCount = totalCategories - shownCategories;

  // ▸ marks bars that drill into a sub-category breakdown. The bucket's
  // own label stays clean (used verbatim in the drill breadcrumb).
  const labels = shown.map((b) => (b.drillable ? `${b.label} ▸` : b.label));
  const values = shown.map((b) => valueOf(b));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
        {subtitle}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <RoughBar
          labels={labels}
          values={values}
          yLabel={metric === 'revenue' ? 'Revenue ($)' : 'Units'}
          color={baseColor}
          roughness={1}
          fontStyle={1}
          width={Math.max(700, labels.length * 70)}
          height={480}
          marginBottom={170}
          axisFontSize="1.15rem"
          tooltipFontSize="1.25rem"
          onBarClick={
            onCategoryClick ? (i) => onCategoryClick(shown[i]) : undefined
          }
        />
      </Box>
      {hiddenCount > 0 && (
        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
          Showing top {shownCategories} of {totalCategories} categories;{' '}
          {hiddenCount} smaller {hiddenCount === 1 ? 'category' : 'categories'} not
          shown.
        </Typography>
      )}
    </Paper>
  );
};

export default CategorySalesReportPage;
